import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { APP_REGEX } from '../constants/static-data.constants';

@Injectable({
  providedIn: 'root'
})
export class DateTimeFormatterService {

  disabledEmergencyHours = {
    day: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
    night: [19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5]
  }

  constructor(private datePipe: DatePipe) { }

  formatDateToString(date: Date): string {
    if (date) {
      const day = ('0' + date.getDate()).slice(-2);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } else {
      return '';
    }
  }

  formatToString(date: Date): string {
    if (date) {
      const day = ('0' + date.getDate()).slice(-2);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    } else {
      return '';
    }
  }

  formatStringToDate(dateString: string): Date {
    // Handle ISO format (yyyy-MM-dd) as well as MM/dd/yyyy
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return new Date(+year, +month - 1, +day);
    }
    const [month, day, year] = dateString.split('/');
    return new Date(+year, +month - 1, +day);
  }

  stringToDate(dateString: string, format: string = 'yyyy-MM-dd'): Date {
    const [year, month, day] = dateString.split('-');
    return new Date(+year, +month - 1, +day);
  }

  formatTimeToString(date: Date): string {
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    return `${hours}:${minutes}`;
  }

  formatStringToTime(timeString: string): Date {
    if (!timeString) {
      return new Date(0, 0, 0, 0, 0, 0);
    }
    const [hours, minutes, seconds] = timeString.split(':');
    return new Date(0, 0, 0, +hours, +minutes, +seconds);
  }

  formatDateTimeToString(dateTime: Date): string {
    return `${this.formatDateToString(dateTime)} ${this.formatTimeToString(dateTime)}`;
  }

  formatDateTime(dateTimeString: string): string {
    return this.datePipe.transform(dateTimeString, 'short') as string
  }

  formatDate(date: Date, format: string): string {
    return this.datePipe.transform(date, format) || '';
  }

  //this will return in format M/d/yy HH:mm:ss
  formatDateTimeNoMs(dateTimeString: string): string {
    return this.datePipe.transform(dateTimeString, 'M/d/yy HH:mm:ss') as string;
  }

  // this will return string value in MM/dd/yyyy HH:mm format
  formatDateTimeNoSeconds(dateTimeString: string): string {
    return this.datePipe.transform(dateTimeString, 'MM/dd/yyyy HH:mm') as string;
  }

  parsePreferredDaysAndTime(input: string) {
    // Regular expression to match the time and days
    const regexDays = APP_REGEX.PREFERRED_DAYS;
    const regexTime = APP_REGEX.PREFERRED_TIME;

    // Execute the regex on the input string
    const matchDays = input.match(regexDays);
    const matchTime = input.match(regexTime);

    // Extracted time and days
    let startTime = null;
    let endTime = null;
    let days: any[] = [];

    if (matchDays) {
      days = matchDays[1].split(',');
    }
    if (matchTime) {
      startTime = matchTime[1];
      endTime = matchTime[2];
    }
    return {
      startTime,
      endTime,
      days
    };
  }


  getDateTimeFromTime(timeString: string | null): Date {
    if (!timeString) {
      throw new Error("Time string cannot be null or undefined.");
    }

    try {
      // Split the time string into time and AM/PM
      const [time, modifier] = timeString.split(' ');

      // Split the time into hours and minutes
      const timeSplit = time.split(':').map(Number);
      let hours = timeSplit[0];
      const minutes = timeSplit[1];

      // Convert 12-hour format to 24-hour format
      if (modifier === 'PM' && hours !== 12) {
        hours += 12;
      } else if (modifier === 'AM' && hours === 12) {
        hours = 0;
      }

      // Create a Date object with the current date and the parsed time
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      date.setSeconds(0); // Set seconds to 0
      date.setMilliseconds(0); // Set milliseconds to 0

      return date;
    } catch (e) {
      console.log(e);
      throw new Error("Invalid time format. Please use 'HH:mm' format.");
    }
  }

  getTimeFromDate(date: Date | null): string {
    if (!date) {
      throw new Error("Date cannot be null or undefined.");
    }

    console.log("datep-----", date);
    try {
      const hours: number = date.getHours();
      const minutes: number = date.getMinutes();
      let modifier = 'AM';

      // Convert hours from 24-hour format to 12-hour format
      const displayHours = hours % 12 || 12; // Converts 0 to 12 for midnight
      if (hours >= 12) {
        modifier = 'PM';
      }

      // Format minutes to always have two digits
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();
      const formattedHrs = displayHours < 10 ? `0${displayHours}` : displayHours.toString();
      // Construct and return the formatted time string
      return `${formattedHrs}:${formattedMinutes} ${modifier}`;
    } catch (e) {
      console.log(e);
      throw new Error("Error extracting time in AM/PM format.");
    }

  }

  getStartAndEndTimes(timeRange: string): { startTime: Date | null, endTime: Date | null } {

    //input format "06:30 AM - 06:30 PM"
    // Step 1: Split the string by ' - ' to get the start and end time strings
    const times = timeRange.split(' - ');

    // Step 2: Trim spaces to get clean time strings
    const startTimeString = times[0].trim();
    const endTimeString = times[1].trim();

    // Step 3: Convert the time strings to Date objects (optional)
    const startTime = this.getDateTimeFromTime(startTimeString);
    const endTime = this.getDateTimeFromTime(endTimeString);
    // const endTime = this.getDateTimeFromTime(endTimeString);

    // check valida date format
    const isValidDate = (date: Date): boolean => !isNaN(date.getTime());

    return {
      startTime: isValidDate(startTime) ? startTime : null,
      endTime: isValidDate(endTime) ? endTime : null
    };

  }

  formatDateToUSDateString(date: Date): string {
    // Returns date in MM/dd/yyyy
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    } as Intl.DateTimeFormatOptions;
    return date instanceof Date ? date.toLocaleDateString('en-US', options) : '';
  }

  formatToDatePartString(str: string) {
    return str.split('T')[0];
  }

  /**
   * serviceDate comes back from the invoice list APIs as a single yyyy-MM-dd date or as a comma
   * separated list of them. Returns the dates as MM/dd/yyyy joined by newlines, so a cell styled
   * with white-space: pre-line shows one date per line instead of one wide row. Entries that
   * cannot be parsed are passed through untouched.
   */
  formatServiceDateList(serviceDate: any): string {
    if (!serviceDate) {
      return '';
    }

    return String(serviceDate)
      .split(',')
      .map((date: string) => date.trim())
      .filter((date: string) => date)
      .map((date: string) => {
        const parsedDate = this.formatStringToDate(date);
        return isNaN(parsedDate.getTime()) ? date : this.formatDateToString(parsedDate);
      })
      .join('\n');
  }

  convertTo12HourFormat(time: string): string {
    if (time) {
      const [hours, minutes] = time.split(":").map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const adjustedHour = hours % 12 || 12;
      const formattedTime = `${adjustedHour}:${minutes < 10 ? '0' + minutes : minutes} ${period}`;
      return formattedTime;
    }
    return '';
  }

  formatTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = '00';

    return `${hours}:${minutes}:${seconds}`;
  }

  createDateInUTC(dateString: string): Date {
    const [year, month, day] = dateString.split("-");
    const formattedDate = `${month}/${day}/${year}`;

    return new Date(formattedDate);
  }

  getDuration(timeIn: Date, timeOut: Date): string {
    if (!timeIn || !timeOut) {
      return "0 mins";
    }

    // Work on copies to avoid mutating inputs
    const start = new Date(timeIn?.getTime());
    let end = new Date(timeOut?.getTime());

    // If end is less than or equal to start, assume the end time is on the next day (e.g., 10:00 PM -> 02:00 AM)
    if (end.getTime() <= start.getTime()) {
      end = new Date(end.getTime() + 24 * 60 * 60 * 1000); // add 24 hours
    }

    const diffMs = end.getTime() - start.getTime();
    const totalMinutes = Math.round(diffMs / (1000 * 60));

    if (totalMinutes === 0) {
      return "0 mins";
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];

    if (hours > 0) {
      parts.push(hours + " " + (hours === 1 ? "hr" : "hrs"));
    }
    if (minutes > 0) {
      parts.push(minutes + " " + (minutes === 1 ? "min" : "mins"));
    }

    return parts.join(" ");
  }

  formatUnitsToHoursMinutes(totalMinutes: number): string {
    if (totalMinutes == null || isNaN(totalMinutes)) {
      return '';
    }
    const mins = Math.round(totalMinutes);
    const sign = mins < 0 ? '-' : '';
    const absMins = Math.abs(mins);
    if (absMins < 60) {
      return `${sign}${absMins} mins`;
    }
    const hours = Math.floor(absMins / 60);
    const minutes = absMins % 60;
    if (minutes === 0) {
      return `${sign}${hours} hrs`;
    }
    return `${sign}${hours} hrs ${minutes} mins`;
  }

  formatDateTimeForFilename(date: Date): string {
    return this.datePipe.transform(date, 'MM-dd-yyyy_HH-mm-ss') || '';
  }
}
